import { HttpClient } from '@angular/common/http';
import { Inject, inject, Injectable, Optional } from '@angular/core';
import {
  AppConfigService,
  AppTokenService,
  AuthAccessTokenDetails,
  logError,
  OauthAEMConfig
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';
import { OAuthFeatureConfig } from '../../models/session.interfaces';

/**
 * Handles fetching the app-specific auth config and obtaining tokens.
 * Used internally by AppAuthGuard.
 *
 * Reads OAUTH_FEATURE_CONFIG to determine which auth config URL to fetch.
 * If not provided, hasConfig() returns false and the guard skips token fetch.
 */
@Injectable({
  providedIn: 'root'
})
export class AppAuthTokenFetcher {
  private appTokenService = inject(AppTokenService);
  private appConfig = inject(AppConfigService);
  private http = inject(HttpClient);

  private _authConfig$!: Observable<OauthAEMConfig>;

  constructor(
    @Optional()
    @Inject('OAUTH_FEATURE_CONFIG')
    private oAuthFeatureConfig: OAuthFeatureConfig
  ) {}

  /** Returns true if OAUTH_FEATURE_CONFIG is provided with valid app/endpointKey */
  hasConfig(): boolean {
    return !!(
      this.oAuthFeatureConfig?.app && this.oAuthFeatureConfig?.endpointKey
    );
  }

  /** Fetches the app-specific token using AppTokenService */
  fetchToken(): Observable<AuthAccessTokenDetails> {
    return this.getAppAuthConfig().pipe(
      switchMap((config: OauthAEMConfig) => {
        return this.appTokenService
          .getAppTokenWithParams(config.CLIENT_ID_VALUE, config.SCOPE_VALUE)
          .pipe(
            catchError((err) => {
              logError(
                err as Error,
                'SKYMILES',
                'SHARED',
                'APP_AUTH_TOKEN_FETCHER'
              );
              return throwError(() => err);
            })
          );
      })
    );
  }

  /** Fetches and caches the app-specific auth config JSON */
  private getAppAuthConfig(): Observable<OauthAEMConfig> {
    if (!this._authConfig$) {
      this._authConfig$ = this.appConfig
        .getConfigValue(
          this.oAuthFeatureConfig.app,
          this.oAuthFeatureConfig.endpointKey
        )
        .pipe(
          switchMap((url: string) => this.http.get<OauthAEMConfig>(url)),
          shareReplay(1)
        );
    }
    return this._authConfig$;
  }
}
